import { Color3, Matrix, Quaternion, Space, Vector3 } from '@babylonjs/core/Maths/math';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Logger } from '@babylonjs/core/Misc/logger';
import { Observable } from '@babylonjs/core/Misc/observable';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Nullable } from '@babylonjs/core/types';
import { SpaceNode } from '@kb3d/models/classes/meshes/space-node';
import { KbVector } from '@kb3d/models/classes/primitives/kb-vector';
import { KbViewer } from '@kb3d/view/kb-viewer';
import { KbAxisDragGizmo } from './axis-drag-gizmo';
import { AUTO_MESH_NAME, KbGizmo } from './gizmo';

/**
 * Gizmo that enables dragging a mesh along 3 axis
 */
export class KbPositionGizmo extends KbGizmo {
    /**
     * Internal gizmo used for interactions on the x axis
     */
    public xGizmo: KbAxisDragGizmo;
    /**
     * Internal gizmo used for interactions on the y axis
     */
    public yGizmo: KbAxisDragGizmo;
    /**
     * Internal gizmo used for interactions on the z axis
     */
    public zGizmo: KbAxisDragGizmo;

    /** Fires an event when any of it's sub gizmos are dragged */
    public onDragStartObservable = new Observable<void>();
    /** Fires an event while any of it's sub gizmos are dragged */
    public onDragObservable = new Observable<{ delta: Vector3 }>();
    /** Fires an event when any of it's sub gizmos are released from dragging */
    public onDragEndObservable = new Observable<{ delta: Vector3 }>();

    private _attachedSpaceNode: SpaceNode | undefined;
    private _positionSpace: TransformNode | undefined;

    attachTo(target: SpaceNode | KbVector, positionSpace?: SpaceNode, orientGizmo?: Quaternion) {
        if (orientGizmo) {
            this.xGizmo.updateGizmoRotationToMatchAttachedMesh = false;
            this.yGizmo.updateGizmoRotationToMatchAttachedMesh = false;
            this.zGizmo.updateGizmoRotationToMatchAttachedMesh = false;
            this.xGizmo._rootMesh.rotationQuaternion = orientGizmo;
            this.yGizmo._rootMesh.rotationQuaternion = orientGizmo;
            this.zGizmo._rootMesh.rotationQuaternion = orientGizmo;
        }
        if (!positionSpace && target instanceof SpaceNode && target._parent) {
            positionSpace = target._parent;
        }
        if (positionSpace) {
            this._positionSpace = this.kbViewer.getRendererNode(positionSpace);
        }
        if (target instanceof SpaceNode) {
            this._attachedSpaceNode = target;
            this._updateGizmoPosition();
        } else {
            this._updateGizmoPosition(target);
        }
    }

    updateGizmoPosition(changeTarget?: KbVector) {
        // Need to wait for the next frame because this can be called before the babylon object is actually updated
        this.kbViewer.onNextFrame(() => {
            this._updateGizmoPosition(changeTarget);
        });
    }

    private _updateGizmoPosition(changeTarget?: KbVector) {
        const positionSpaceTransform = this._positionSpace?.computeWorldMatrix(true);
        if (changeTarget) {
            this.attachMeshAtNodeSpace(this.gizmoLayer.utilityLayerScene, changeTarget, positionSpaceTransform);
        } else if (this._attachedSpaceNode) {
            this.attachMeshAtNodeSpace(
                this.gizmoLayer.utilityLayerScene,
                this._attachedSpaceNode,
                positionSpaceTransform
            );
        }
        super.updateGizmoPosition();
    }

    public get attachedMesh() {
        if (this.xGizmo) {
            return this.xGizmo.attachedMesh;
        } else if (this.yGizmo) {
            return this.yGizmo.attachedMesh;
        } else {
            return this.zGizmo.attachedMesh;
        }
    }

    public set attachedMesh(mesh: Nullable<AbstractMesh>) {
        if (this.xGizmo) {
            this.xGizmo.attachedMesh = mesh;
        }
        if (this.yGizmo) {
            this.yGizmo.attachedMesh = mesh;
        }
        if (this.zGizmo) {
            this.zGizmo.attachedMesh = mesh;
        }
        if (mesh) {
            this._rootMesh.rotationQuaternion = mesh.rotationQuaternion
                ? mesh.rotationQuaternion
                : mesh.rotation.toQuaternion();
            this._rootMesh.position = mesh.position;
            this._rootMesh.setPivotPoint(mesh.getPivotPoint(), Space.WORLD);
        }
    }

    get visibility() {
        return this.xGizmo.visibility;
    }
    set visibility(visibility: number) {
        if (this.xGizmo) {
            this.xGizmo.visibility = visibility;
            this.yGizmo.visibility = visibility;
            this.zGizmo.visibility = visibility;
        }
    }

    private positionChange = Vector3.Zero();

    /**
     * Creates a PositionGizmo
     * @param gizmoLayer The utility layer the gizmo will be added to
     */
    constructor(
        private kbViewer: KbViewer,
        gizmoLayer: UtilityLayerRenderer = UtilityLayerRenderer.DefaultUtilityLayer,
        axisVisibility?: [boolean, boolean, boolean]
    ) {
        super(gizmoLayer);
        let activeGizmos = [];
        if (axisVisibility) {
            if (axisVisibility[0]) {
                this.xGizmo = new KbAxisDragGizmo(new Vector3(1, 0, 0), Color3.Red().scale(0.5), gizmoLayer);
                this.xGizmo.updateGizmoRotationToMatchAttachedMesh = axisVisibility[0];
                this.xGizmo.updateGizmoPositionToMatchAttachedMesh = axisVisibility[0];
                activeGizmos.push(this.xGizmo);
            }
            if (axisVisibility[1]) {
                this.yGizmo = new KbAxisDragGizmo(new Vector3(0, 1, 0), Color3.Green().scale(0.5), gizmoLayer);
                this.yGizmo.updateGizmoRotationToMatchAttachedMesh = axisVisibility[1];
                this.yGizmo.updateGizmoPositionToMatchAttachedMesh = axisVisibility[1];
                activeGizmos.push(this.yGizmo);
            }
            if (axisVisibility[2]) {
                this.zGizmo = new KbAxisDragGizmo(new Vector3(0, 0, 1), Color3.Blue().scale(0.5), gizmoLayer);
                this.zGizmo.updateGizmoRotationToMatchAttachedMesh = axisVisibility[2];
                this.zGizmo.updateGizmoPositionToMatchAttachedMesh = axisVisibility[2];
                activeGizmos.push(this.zGizmo);
            }
        } else {
            this.xGizmo = new KbAxisDragGizmo(new Vector3(1, 0, 0), Color3.Red().scale(0.5), gizmoLayer);
            this.yGizmo = new KbAxisDragGizmo(new Vector3(0, 1, 0), Color3.Green().scale(0.5), gizmoLayer);
            this.zGizmo = new KbAxisDragGizmo(new Vector3(0, 0, 1), Color3.Blue().scale(0.5), gizmoLayer);

            this.xGizmo.updateGizmoRotationToMatchAttachedMesh = true;
            this.yGizmo.updateGizmoRotationToMatchAttachedMesh = true;
            this.zGizmo.updateGizmoRotationToMatchAttachedMesh = true;
            this.xGizmo.updateGizmoPositionToMatchAttachedMesh = true;
            this.yGizmo.updateGizmoPositionToMatchAttachedMesh = true;
            this.zGizmo.updateGizmoPositionToMatchAttachedMesh = true;
            activeGizmos = [this.xGizmo, this.yGizmo, this.zGizmo];
        }

        // Relay drag events
        activeGizmos.forEach(gizmo => {
            gizmo.dragBehavior.onDragStartObservable.add(() => {
                this.onDragStartObservable.notifyObservers();
                this.positionChange.setAll(0);
            });
            gizmo.updateObservable.add(event => {
                // transform the rotation to be in local space, the gizmos return global space movements
                const transform = new Matrix();
                this.attachedMesh!.computeWorldMatrix(true).invertToRef(transform);
                transform.setTranslation(Vector3.Zero());
                const { delta } = event;
                const result = Vector3.TransformCoordinates(delta, transform);

                // squish floating point errors that look gross in the output
                if (Math.abs(result.x) < 0.00000001) result.x = 0;
                if (Math.abs(result.y) < 0.00000001) result.y = 0;
                if (Math.abs(result.z) < 0.00000001) result.z = 0;

                this.positionChange.addInPlace(result);
                this.onDragObservable.notifyObservers({ delta: result });
            });
            gizmo.dragBehavior.onDragEndObservable.add(() => {
                this.onDragEndObservable.notifyObservers({ delta: this.positionChange });
            });
        });
        this.attachedMesh = null;
    }

    /**
     * Drag distance in babylon units that the gizmo will snap to when dragged (Default: 0)
     */
    public set snapDistance(value: number) {
        if (this.xGizmo) {
            this.xGizmo.snapDistance = value;
        }
        if (this.yGizmo) {
            this.yGizmo.snapDistance = value;
        }
        if (this.zGizmo) {
            this.zGizmo.snapDistance = value;
        }
    }
    public get snapDistance() {
        if (this.xGizmo) {
            return this.xGizmo.snapDistance;
        } else if (this.yGizmo) {
            return this.yGizmo.snapDistance;
        } else {
            return this.zGizmo.snapDistance;
        }
    }

    /**
     * Ratio for the scale of the gizmo (Default: 1)
     */
    public set scaleRatio(value: number) {
        if (this.xGizmo) {
            this.xGizmo.scaleRatio = value;
        }
        if (this.yGizmo) {
            this.yGizmo.scaleRatio = value;
        }
        if (this.zGizmo) {
            this.zGizmo.scaleRatio = value;
        }
    }
    public get scaleRatio() {
        if (this.xGizmo) {
            return this.xGizmo.scaleRatio;
        } else if (this.yGizmo) {
            return this.yGizmo.scaleRatio;
        } else {
            return this.zGizmo.scaleRatio;
        }
    }

    /**
     * Disposes of the gizmo
     */
    public dispose() {
        if (this.xGizmo) {
            this.xGizmo.dispose();
        }
        if (this.yGizmo) {
            this.yGizmo.dispose();
        }
        if (this.zGizmo) {
            this.zGizmo.dispose();
        }
        this.onDragStartObservable.clear();
        this.onDragEndObservable.clear();
        if (this.attachedMesh && this.attachedMesh.name === AUTO_MESH_NAME) {
            this.attachedMesh.dispose();
        }
    }

    /**
     * CustomMeshes are not supported by this gizmo
     * @param mesh The mesh to replace the default mesh of the gizmo
     */
    public setCustomMesh(mesh: Mesh) {
        Logger.Error(
            'Custom meshes are not supported on this gizmo, please set the custom meshes on the gizmos contained within this one (gizmo.xGizmo, gizmo.yGizmo, gizmo.zGizmo)'
        );
    }
}
